import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { fmtEUR } from '@/lib/utils';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2, CheckCircle, XCircle, Clock, Package, PlusCircle,
  MapPin, Mail, Phone, Globe, FileText, Boxes, Pencil, Trash2, MoreVertical, Eye
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import axios from 'axios';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const config = {
    pending: { icon: Clock, label: 'Genehmigung ausstehend', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    approved: { icon: CheckCircle, label: 'Genehmigt', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    rejected: { icon: XCircle, label: 'Abgelehnt', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  return (
    <Badge className={`gap-1.5 text-sm px-3 py-1 ${c.className}`} data-testid="company-status-badge">
      <Icon className="h-4 w-4" /> {c.label}
    </Badge>
  );
};

const ExternalPortalPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const companyRes = await axios.get(`${API}/external-companies/me`);
      setCompany(companyRes.data);

      if (companyRes.data.approved_status === 'approved' && user?.site_id) {
        try {
          const matsRes = await axios.get(`${API}/materials/my-site`, { params: { limit: 100 } });
          setMaterials(matsRes.data.items || []);
        } catch { setMaterials([]); }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(t('ext.noCompanyLinked') || 'Kein externes Unternehmen verknüpft');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (materialId, title) => {
    setDeleting(materialId);
    try {
      await axios.delete(`${API}/materials/${materialId}`);
      toast.success(t('ext.materialDeleted') || `"${title}" wurde gelöscht`);
      setMaterials(prev => prev.filter(m => m.material_id !== materialId));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error') || 'Fehler beim Löschen');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!company) return null;
  const isApproved = company.approved_status === 'approved';
  const isPending = company.approved_status === 'pending';
  const isRejected = company.approved_status === 'rejected';

  return (
    <div className="min-h-screen bg-background" data-testid="external-portal-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Company Info Card */}
        <Card data-testid="company-info-card">
          <CardHeader>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">{company.company_name}</CardTitle>
                  <CardDescription>{t('ext.portalTitle') || 'Unternehmensportal'}</CardDescription>
                </div>
              </div>
              <StatusBadge status={company.approved_status} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>{company.street} {company.house_number}, {company.postal_code} {company.city}, {company.country}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4 shrink-0" />
                <span>{company.contact_email}</span>
              </div>
              {company.contact_phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>{company.contact_phone}</span>
                </div>
              )}
              {company.website_optional && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Globe className="h-4 w-4 shrink-0" />
                  <a href={company.website_optional} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">{company.website_optional}</a>
                </div>
              )}
              {company.vat_id_optional && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="h-4 w-4 shrink-0" />
                  <span>USt-IdNr.: {company.vat_id_optional}</span>
                </div>
              )}
              {company.industry_optional && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Package className="h-4 w-4 shrink-0" />
                  <span>{company.industry_optional}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Messages */}
        {isPending && (
          <Card className="border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10" data-testid="pending-notice">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Clock className="h-8 w-8 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-lg text-yellow-800 dark:text-yellow-300">
                    {t('ext.pendingTitle') || 'Genehmigung ausstehend'}
                  </h3>
                  <p className="text-yellow-700 dark:text-yellow-400 mt-1">
                    {t('ext.pendingMessage') || 'Ihre Registrierung wird derzeit von unserem Team geprüft. Sobald Ihr Unternehmen genehmigt wurde, können Sie Materialien auf dem Marktplatz anbieten.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isRejected && (
          <Card className="border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10" data-testid="rejected-notice">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <XCircle className="h-8 w-8 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-lg text-red-800 dark:text-red-300">
                    {t('ext.rejectedTitle') || 'Registrierung abgelehnt'}
                  </h3>
                  <p className="text-red-700 dark:text-red-400 mt-1">
                    {t('ext.rejectedMessage') || 'Leider wurde Ihre Registrierung abgelehnt. Bitte kontaktieren Sie den Support für weitere Informationen.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approved: Materials Management */}
        {isApproved && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="font-manrope text-xl font-bold">{t('ext.myListings') || 'Meine Angebote'}</h2>
              <Button className="bg-orange-500 hover:bg-orange-600 gap-1.5" onClick={() => navigate('/material/new')} data-testid="create-material-btn">
                <PlusCircle className="h-4 w-4" />
                {t('ext.createListing') || 'Material einstellen'}
              </Button>
            </div>

            {materials.length === 0 ? (
              <Card data-testid="no-materials-card">
                <CardContent className="p-12 text-center">
                  <Boxes className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-semibold text-lg mb-2">{t('ext.noListings') || 'Noch keine Materialien eingestellt'}</h3>
                  <p className="text-muted-foreground mb-6">
                    {t('ext.noListingsDesc') || 'Stellen Sie Ihr erstes Material ein, um es auf dem Marktplatz anzubieten.'}
                  </p>
                  <Button className="bg-orange-500 hover:bg-orange-600 gap-1.5" onClick={() => navigate('/material/new')} data-testid="create-first-material-btn">
                    <PlusCircle className="h-4 w-4" />
                    {t('ext.createFirstListing') || 'Erstes Material einstellen'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4" data-testid="materials-list">
                {materials.map((m) => (
                  <Card key={m.material_id} className="hover:border-orange-300 transition-colors" data-testid={`material-card-${m.material_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/material/${m.material_id}`)}>
                          <h3 className="font-semibold truncate">{m.common_title}</h3>
                          <p className="text-sm text-muted-foreground">{m.manufacturer_name} &middot; {m.manufacturer_part_number}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{m.sku_count} SKU(s)</span>
                            <span>{m.total_quantity} {m.skus?.[0]?.uom || 'kg'}</span>
                            {m.total_value_kEUR > 0 && <span>{fmtEUR(m.total_value_kEUR)} EUR</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={
                            m.external_approval_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            m.external_approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                            m.status === 'available' ? 'bg-green-100 text-green-800' :
                            m.status === 'documentation_missing' ? 'bg-yellow-100 text-yellow-800' :
                            m.status === 'archived' ? 'bg-slate-100 text-slate-600' :
                            'bg-slate-100 text-slate-800'
                          } data-testid={`material-status-${m.material_id}`}>
                            {m.external_approval_status === 'pending' ? (t('status.pendingApproval') || 'Freigabe ausstehend') :
                             m.external_approval_status === 'rejected' ? (t('status.rejected') || 'Abgelehnt') :
                             m.status === 'available' ? (t('status.available') || 'Verfügbar') :
                             m.status === 'documentation_missing' ? (t('status.documentation_missing') || 'Dok. fehlt') :
                             m.status === 'archived' ? (t('status.archived') || 'Archiviert') :
                             m.status}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`material-actions-${m.material_id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/material/${m.material_id}`)} data-testid={`view-material-${m.material_id}`}>
                                <Eye className="h-4 w-4 mr-2" />
                                {t('common.view') || 'Ansehen'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/material/${m.material_id}/edit`)} data-testid={`edit-material-${m.material_id}`}>
                                <Pencil className="h-4 w-4 mr-2" />
                                {t('common.edit') || 'Bearbeiten'}
                              </DropdownMenuItem>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600 focus:text-red-600" data-testid={`delete-trigger-${m.material_id}`}>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t('common.delete') || 'Löschen'}
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>{t('ext.deleteConfirmTitle') || 'Material löschen?'}</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {t('ext.deleteConfirmDesc') || `Sind Sie sicher, dass Sie "${m.common_title}" löschen möchten? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>{t('common.cancel') || 'Abbrechen'}</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(m.material_id, m.common_title)}
                                      className="bg-red-600 hover:bg-red-700"
                                      disabled={deleting === m.material_id}
                                      data-testid={`confirm-delete-${m.material_id}`}
                                    >
                                      {deleting === m.material_id ? (t('common.deleting') || 'Löschen...') : (t('common.delete') || 'Löschen')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalPortalPage;
